const RATNIVA_DISCOUNT_STORAGE_KEY = 'ratniva-active-discount-code';

const setFastrrDiscountCookie = (code) => {
  const normalizedCode = String(code || '').trim();
  const value = normalizedCode ? encodeURIComponent(normalizedCode) : '';
  const expiry = normalizedCode ? '' : '; Max-Age=0';
  document.cookie = `discount_code=${value}; Path=/; SameSite=Lax${expiry}`;
};

const getActiveDiscountCode = (scope = document) => {
  const renderedDiscount = scope.querySelector?.('[data-active-discount-code]');
  if (renderedDiscount) {
    const renderedCode = renderedDiscount.dataset.activeDiscountCode?.trim() || '';
    if (!renderedCode) {
      sessionStorage.removeItem(RATNIVA_DISCOUNT_STORAGE_KEY);
      setFastrrDiscountCookie('');
    }
    return renderedCode;
  }

  return sessionStorage.getItem(RATNIVA_DISCOUNT_STORAGE_KEY) || '';
};

const playCouponSparkle = (scope) => {
  scope?.playCelebration?.(scope.querySelector('[data-offers-open]'));
};

if (!customElements.get('cart-offers')) {
  customElements.define(
    'cart-offers',
    class CartOffers extends HTMLElement {
      connectedCallback() {
        this.autoApplyFrame = requestAnimationFrame(() => this.autoApplyMilestone());
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.autoApplyFrame);
      }

      autoApplyMilestone() {
        const drawer = this.closest('cart-drawer');
        const progress = drawer?.querySelector('cart-coupon-progress');
        if (!progress || drawer.autoCouponPending || this.applying) return;
        const key = progress.dataset.autoCouponKey;
        if (drawer.lastAutoCouponKey === key) return;
        const code = progress.dataset.autoCoupon || '';
        const activeCode = getActiveDiscountCode(drawer);
        const managedCodes = progress.dataset.milestoneCodes.split(',').filter(Boolean);
        drawer.lastAutoCouponKey = key;
        if (code.toUpperCase() === activeCode.toUpperCase()) return;
        if (!code && !managedCodes.some((managed) => managed.toUpperCase() === activeCode.toUpperCase())) return;
        this.applyOffer(undefined, { automatic: true, code });
      }

      get panel() {
        return this.querySelector('[role="dialog"]');
      }

      get input() {
        return this.querySelector('[data-offer-input]');
      }

      get status() {
        return this.querySelector('[data-offer-status]');
      }

      openPanel() {
        if (!this.panel || !this.panel.hidden) return;
        this.panel.hidden = false;
        this.querySelector('[data-offers-open]')?.setAttribute('aria-expanded', 'true');
        this.querySelector('[data-offers-close]')?.focus();
      }

      closePanel({ restoreFocus = true } = {}) {
        if (!this.panel || this.panel.hidden) return;
        this.panel.hidden = true;
        const trigger = this.querySelector('[data-offers-open]');
        trigger?.setAttribute('aria-expanded', 'false');
        const drawer = this.closest('cart-drawer');
        if (drawer) {
          drawer.classList.add('animate', 'active');
          document.body.classList.add('overflow-hidden');
        }
        if (restoreFocus) trigger?.focus();
      }

      selectOffer(offer) {
        if (!this.input || !this.contains(offer)) return;
        this.querySelectorAll('[data-offer-code]').forEach((item) => item.classList.remove('is-selected'));
        offer.classList.add('is-selected');
        this.input.value = offer.dataset.offerCode;
      }

      async applyOffer(triggerButton, options = {}) {
        const automatic = options.automatic === true;
        const code = (automatic ? options.code : this.input?.value.trim())?.toUpperCase() || '';
        const button = triggerButton || this.querySelector('[data-offer-apply]');
        const ownerDrawer = this.closest('cart-drawer');
        if ((!code && !automatic) || !button || button.disabled || this.applying || ownerDrawer?.autoCouponPending) return;
        this.applying = true;
        if (ownerDrawer) ownerDrawer.autoCouponPending = true;
        const originalLabel = button.textContent;

        button.disabled = true;
        button.textContent = 'Applying…';
        this.showStatus('Checking coupon…');

        try {
          const response = await fetch(routes.cart_update_url, {
            ...fetchConfig(),
            signal: AbortSignal.timeout(10000),
            body: JSON.stringify({
              discount: code,
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname,
            }),
          });
          if (!response.ok) throw new Error('Cart update failed');

          const cart = await response.json();
          const discountValues = [
            ...(cart.discount_codes || [])
              .filter((discount) => discount.applicable !== false)
              .flatMap((discount) => [discount.code, discount.title]),
            ...(cart.cart_level_discount_applications || []).flatMap((discount) => [discount.code, discount.title]),
            ...(cart.items || []).flatMap((item) =>
              (item.line_level_discount_allocations || []).flatMap((allocation) => [
                allocation.discount_application?.code,
                allocation.discount_application?.title,
              ])
            ),
          ]
            .filter(Boolean)
            .map((value) => String(value).toUpperCase());
          const applied = code ? discountValues.includes(code) : true;
          if (ownerDrawer && (!this.isConnected || !cart.sections?.['cart-drawer'])) {
            await ownerDrawer.querySelector('cart-drawer-items')?.onCartUpdate();
            return;
          }

          if (!applied) {
            sessionStorage.removeItem(RATNIVA_DISCOUNT_STORAGE_KEY);
            setFastrrDiscountCookie('');
            const drawer = ownerDrawer;
            if (drawer && cart.sections) {
              drawer.renderContents(cart, false);
              const offers = drawer.querySelector('cart-offers');
              if (offers?.input) offers.input.value = code;
              offers?.showStatus(`Coupon ${code} is not applicable to this cart.`);
              offers?.openPanel();
            } else {
              window.location.reload();
            }
            return;
          }

          if (code) sessionStorage.setItem(RATNIVA_DISCOUNT_STORAGE_KEY, code);
          else sessionStorage.removeItem(RATNIVA_DISCOUNT_STORAGE_KEY);
          setFastrrDiscountCookie(code);
          const drawer = ownerDrawer;
          if (drawer && cart.sections) {
            drawer.renderContents(cart, !automatic);
            if (!automatic) playCouponSparkle(drawer);
          } else {
            window.location.reload();
          }
        } catch (error) {
          this.showStatus('Coupon could not be applied. Please try again.');
          this.openPanel();
          button.disabled = false;
          button.textContent = 'Apply';
        } finally {
          this.applying = false;
          button.disabled = false;
          button.textContent = originalLabel;
          if (ownerDrawer) {
            ownerDrawer.autoCouponPending = false;
            ownerDrawer.querySelector('cart-offers')?.autoApplyMilestone();
            if (!ownerDrawer.autoCouponPending && ownerDrawer.queuedCouponCheckout) {
              const queued = ownerDrawer.queuedCouponCheckout;
              ownerDrawer.queuedCouponCheckout = null;
              const checkout = queued.id
                ? document.getElementById(queued.id)
                : ownerDrawer.querySelector(queued.selector);
              if (checkout && ownerDrawer.contains(checkout) && !checkout.disabled) checkout.click();
            }
          }
        }
      }

      showStatus(message) {
        if (!this.status) return;
        this.status.textContent = message;
        this.status.hidden = false;
      }
    }
  );
}

if (!window.ratnivaCartOffersEventsBound) {
  window.ratnivaCartOffersEventsBound = true;

  document.addEventListener('click', (event) => {
    const selector = 'button[name="checkout"], input[name="checkout"], .sr-headless-checkout, button[name="sr-headless-button"]';
    const checkout = event.target.closest(selector);
    const drawer = checkout?.closest('cart-drawer');
    if (!drawer?.autoCouponPending || checkout.disabled) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    drawer.queuedCouponCheckout = { id: checkout.id, selector };
  }, true);

  document.addEventListener(
    'click',
    (event) => {
      const checkout = event.target.closest('.sr-headless-checkout, button[name="sr-headless-button"]');
      if (!checkout || checkout.disabled) return;

      const scope = checkout.closest('cart-drawer') || document;
      const code = getActiveDiscountCode(scope);
      if (code) setFastrrDiscountCookie(code);
    },
    true
  );

  document.addEventListener('click', (event) => {
    const quickApply = event.target.closest('[data-offer-quick-apply]');
    if (quickApply) {
      event.preventDefault();
      const offers = quickApply.closest('cart-offers');
      if (offers?.input) {
        offers.input.value = quickApply.dataset.offerQuickApply;
        offers.applyOffer(quickApply);
      }
      return;
    }

    const openButton = event.target.closest('[data-offers-open]');
    if (openButton) {
      event.preventDefault();
      openButton.closest('cart-offers')?.openPanel();
      return;
    }

    const closeButton = event.target.closest('[data-offers-close]');
    if (closeButton) {
      event.preventDefault();
      event.stopPropagation();
      closeButton.closest('cart-offers')?.closePanel();
      return;
    }

    const offer = event.target.closest('[data-offer-code]');
    if (offer) {
      event.preventDefault();
      offer.closest('cart-offers')?.selectOffer(offer);
      return;
    }

    const checkout = event.target.closest('button[name="checkout"], input[name="checkout"]');
    if (!checkout || checkout.disabled) return;
    const scope = checkout.closest('cart-drawer') || document;
    const code = getActiveDiscountCode(scope);
    if (!code) return;

    event.preventDefault();
    setFastrrDiscountCookie(code);
    const root = window.Shopify?.routes?.root || '/';
    window.location.assign(
      `${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(`${root}checkout`)}`
    );
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-offer-form]');
    if (!form) return;
    event.preventDefault();
    form.closest('cart-offers')?.applyOffer();
  });

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape') return;
      const openOffers = document.querySelector('cart-offers [role="dialog"]:not([hidden])');
      if (!openOffers) return;
      event.preventDefault();
      event.stopPropagation();
      window.ratnivaOfferEscapeHandled = true;
      openOffers.closest('cart-offers')?.closePanel();
    },
    true
  );

  document.addEventListener(
    'keyup',
    (event) => {
      if (event.key !== 'Escape' || !window.ratnivaOfferEscapeHandled) return;
      event.preventDefault();
      event.stopPropagation();
      window.ratnivaOfferEscapeHandled = false;
    },
    true
  );
}
